/**
 * Sensitivity engine — how much does the break-even year move under small
 * perturbations of the four levers the user has the least confidence in?
 *
 * Levers (matches REN-15 verbatim):
 *   ± 1.0%  — home appreciation
 *   ± 0.5%  — mortgage rate
 *   ± 1.0%  — rent inflation
 *   ± 1.0%  — discount / investment return
 *
 * For each lever we run the break-even engine three times: at `baseline − delta`,
 * at `baseline` (unchanged), and at `baseline + delta`. The two perturbed years
 * become the low/high ends of the tornado bar the UI draws in `ui/tornado.ts`.
 *
 * The function is pure and deterministic — the tornado chart is a slice of
 * scenario space, not a Monte Carlo — so a golden-number test can pin every
 * perturbation to the year without needing to mock anything.
 *
 * Design notes:
 *  • Perturbed inputs are floored at zero (a −1% rent inflation on a 0.5%
 *    baseline would otherwise go negative, which the engine treats as
 *    deflation and produces confusing tornado bars from). The tornado
 *    caption in the UI explains this so a reader isn't surprised when a
 *    lever's `low` end matches the baseline exactly.
 *  • `null` on `low` / `high` / `baseline` means "no break-even inside the
 *    horizon" — the tornado renderer treats it as "never" on the year axis.
 *  • The perturbation deltas live in `SENSITIVITY_AXES` so the tests can
 *    iterate them without hard-coding each key.
 */

import { computeBreakEven, type BreakEvenInputs } from "./breakEven";

type BreakEvenLever =
  | "appreciation"
  | "mortgageRate"
  | "rentInflation"
  | "discountRate";

/** One perturbation axis. `delta` is the ± amount applied around the baseline (in percentage points). */
export interface SensitivityAxis {
  readonly key: BreakEvenLever;
  readonly label: string;
  readonly delta: number;
  readonly unit: string;
}

/** One tornado row — the low / baseline / high break-even year for a single lever. */
export interface SensitivityRow {
  readonly key: BreakEvenLever;
  readonly label: string;
  readonly delta: number;
  readonly unit: string;
  /** Break-even year at the unperturbed baseline. `null` = never within horizon. */
  readonly baseline: number | null;
  /** Break-even year with the lever moved down by `delta`. `null` = never within horizon. */
  readonly low: number | null;
  /** Break-even year with the lever moved up by `delta`. `null` = never within horizon. */
  readonly high: number | null;
  /** Actual perturbed value on the "low" side (after the floor-at-zero rule). Useful for the UI caption. */
  readonly lowValue: number;
  /** Actual perturbed value on the "high" side. */
  readonly highValue: number;
  /** Baseline value of this lever (echoed for the UI). */
  readonly baselineValue: number;
}

/**
 * The four levers named in REN-15. Order is stable so a reader who has seen
 * the tornado chart before finds the same rows in the same positions — the UI
 * layer can re-sort by magnitude for the actual render.
 */
export const SENSITIVITY_AXES: readonly SensitivityAxis[] = [
  { key: "appreciation", label: "Home appreciation", delta: 1, unit: "%/yr" },
  { key: "mortgageRate", label: "Mortgage rate", delta: 0.5, unit: "%" },
  { key: "rentInflation", label: "Rent inflation", delta: 1, unit: "%/yr" },
  { key: "discountRate", label: "Investment return", delta: 1, unit: "%/yr" },
];

/**
 * Compute the sensitivity table for a scenario. Returns one row per axis in
 * `SENSITIVITY_AXES` order.
 */
export function computeSensitivity(base: BreakEvenInputs): readonly SensitivityRow[] {
  const baseline = computeBreakEven(base).breakEvenYear;
  const rows: SensitivityRow[] = [];
  for (const axis of SENSITIVITY_AXES) {
    const baselineValue = readLever(base, axis.key);
    const lowValue = Math.max(0, baselineValue - axis.delta);
    const highValue = baselineValue + axis.delta;
    const low = computeBreakEven(setLever(base, axis.key, lowValue)).breakEvenYear;
    const high = computeBreakEven(setLever(base, axis.key, highValue)).breakEvenYear;
    rows.push({
      key: axis.key,
      label: axis.label,
      delta: axis.delta,
      unit: axis.unit,
      baseline,
      low,
      high,
      baselineValue,
      lowValue,
      highValue,
    });
  }
  return rows;
}

function readLever(inputs: BreakEvenInputs, lever: BreakEvenLever): number {
  switch (lever) {
    case "appreciation":
      return inputs.annualHomeAppreciationPercent;
    case "mortgageRate":
      return inputs.annualInterestRatePercent;
    case "rentInflation":
      return inputs.annualRentInflationPercent;
    case "discountRate":
      return inputs.annualDiscountRatePercent;
  }
}

function setLever(
  inputs: BreakEvenInputs,
  lever: BreakEvenLever,
  value: number,
): BreakEvenInputs {
  switch (lever) {
    case "appreciation":
      return { ...inputs, annualHomeAppreciationPercent: value };
    case "mortgageRate":
      return { ...inputs, annualInterestRatePercent: value };
    case "rentInflation":
      return { ...inputs, annualRentInflationPercent: value };
    case "discountRate":
      return { ...inputs, annualDiscountRatePercent: value };
  }
}
